// ⚠️ AUTOPISTA_MARKER · LEGACY / SOLO CONSULTIVO
// NO USAR COMO FUENTE NUEVA DE INGRESOS.
// La fuente de verdad financiera debe ser db.cobros.
// Este componente se mantiene por compatibilidad histórica hasta migración/derivación controlada.
// No ampliar ni crear nuevas estadísticas sobre esta colección sin autorización explícita de Ann.
// Doc: docs/LA_AUTOPISTA_CAMINOS_MARCADOS.md §3 (cobros_presenciales = AUTOPISTA_CAMINO_PARALELO)
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Search, ExternalLink, UserX, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { navigateToClientWithReturn } from '../../utils/clientNavContext';

const API = '/api';

const MET_LABEL = { efectivo: "Efectivo", tarjeta: "Tarjeta", transferencia: "Transf.", bizum: "Bizum", varios: "Varios" };

const DeudaPresencialPanel = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const sedeActiva = auth?.sedeActiva;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [filtroMetodo, setFiltroMetodo] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("activos"); // activos | bajas
  const [pagarModal, setPagarModal] = useState(null); // { deudor }
  const [pagarMetodo, setPagarMetodo] = useState("efectivo");
  const [pagarFecha, setPagarFecha] = useState(new Date().toISOString().split("T")[0]);
  const [pagando, setPagando] = useState(false);

  const handlePagar = async () => {
    if (!pagarModal) return;
    setPagando(true);
    try {
      // Confirmar cada cobro pendiente del deudor
      for (const cobro_id of pagarModal.cobro_ids) {
        const params = new URLSearchParams({ metodo_pago: pagarMetodo, fecha_cobro_real: pagarFecha });
        await axios.post(`${API}/cobros/${cobro_id}/confirmar?${params.toString()}`);
      }
      toast.success(`${pagarModal.nombre}: ${pagarModal.cobro_ids.length} cobro(s) confirmado(s)`);
      setPagarModal(null);
      fetchDeuda();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al confirmar cobro");
    } finally {
      setPagando(false);
    }
  };

  const fetchDeuda = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = {};
      if (sedeActiva && sedeActiva !== "TODAS") params.sede = sedeActiva;
      if (filtroMetodo) params.metodo = filtroMetodo;
      const res = await axios.get(`${API}/cobros/deuda-presencial`, { params });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Error desconocido";
      setFetchError(`Error ${err?.response?.status || ""}: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [sedeActiva, filtroMetodo]);

  useEffect(() => { fetchDeuda(); }, [fetchDeuda]);

  const allDeudores = (data?.deudores || []).filter(d => {
    if (!search.trim()) return true;
    return d.nombre.toLowerCase().includes(search.toLowerCase());
  });

  const activos = allDeudores.filter(d => !d.es_baja);
  const bajas = allDeudores.filter(d => d.es_baja);
  const deudores = tab === "activos" ? activos : bajas;

  const renderTable = (list, emptyMsg) => {
    if (list.length === 0) {
      return <div style={{ padding: "20px", textAlign: "center", color: "#16a34a", fontSize: "14px", fontWeight: 600 }}>{emptyMsg}</div>;
    }
    return (
      <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Cliente</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Meses pendientes</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Importe</th>
              <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Método</th>
              <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Estado</th>
              <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {list.map((d, idx) => (
              <tr key={d.cliente_id} style={{ borderBottom: "1px solid #f1f5f9", backgroundColor: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                <td style={{ padding: "8px 10px" }}>
                  <div style={{ fontWeight: 600 }}>{d.nombre}</div>
                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>{d.sede}</div>
                </td>
                <td style={{ padding: "8px 10px", fontSize: "12px", color: "#64748b", maxWidth: "250px" }}>
                  {d.meses_texto}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: d.meses_deuda > 3 ? "#dc2626" : "#1e293b" }}>
                  {d.total_importe.toFixed(2)}€
                  {d.meses_deuda > 3 && (
                    <span style={{ display: "block", fontSize: "10px", fontWeight: 600, color: "#dc2626" }}>{d.meses_deuda} meses</span>
                  )}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "center", fontSize: "12px" }}>
                  {MET_LABEL[d.metodo_pago] || d.metodo_pago}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "center" }}>
                  {d.es_baja ? (
                    <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", backgroundColor: "#f3e8ff", color: "#7c3aed", border: "1px solid #d8b4fe" }}>Baja irregular</span>
                  ) : d.en_is ? (
                    <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", backgroundColor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" }}>IS</span>
                  ) : d.meses_deuda > 3 ? (
                    <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", backgroundColor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>Moroso</span>
                  ) : (
                    <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 6px", borderRadius: "4px", backgroundColor: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}>Pendiente</span>
                  )}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "center", display: "flex", gap: "4px", justifyContent: "center" }}>
                  {!d.es_baja && d.cobro_ids?.length > 0 && (
                    <button onClick={() => { setPagarModal(d); setPagarMetodo(d.metodo_pago || "efectivo"); setPagarFecha(new Date().toISOString().split("T")[0]); }}
                      title="Confirmar pago" style={{ background: "none", border: "none", cursor: "pointer", color: "#16a34a", padding: "3px" }}>
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => navigateToClientWithReturn(navigate, d.cliente_id, 'deuda-presencial')}
                    title="Ver ficha" style={{ background: "none", border: "none", cursor: "pointer", color: "#3b82f6", padding: "3px" }}>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: "8px 10px", borderTop: "2px solid #e2e8f0", backgroundColor: "#f8fafc", display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
          <span style={{ color: "#64748b" }}>{list.length} clientes</span>
          <span style={{ fontWeight: 700 }}>Total: {list.reduce((s, d) => s + d.total_importe, 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })}€</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* Tabs */}
      <div style={{ display: "flex", gap: "0", marginBottom: "16px", borderBottom: "2px solid #e2e8f0" }}>
        <button onClick={() => setTab("activos")}
          style={{ padding: "10px 20px", fontSize: "14px", fontWeight: 600, border: "none", cursor: "pointer",
            borderBottom: tab === "activos" ? "3px solid #dc2626" : "3px solid transparent",
            color: tab === "activos" ? "#dc2626" : "#64748b", background: "none" }}>
          <AlertTriangle className="w-4 h-4" style={{ display: "inline", verticalAlign: "middle", marginRight: "6px" }} />
          Cobros pendientes {activos.length > 0 && <span style={{ fontSize: "12px", backgroundColor: "#fef2f2", color: "#dc2626", padding: "1px 6px", borderRadius: "10px", marginLeft: "6px" }}>{activos.length}</span>}
        </button>
        <button onClick={() => setTab("bajas")}
          style={{ padding: "10px 20px", fontSize: "14px", fontWeight: 600, border: "none", cursor: "pointer",
            borderBottom: tab === "bajas" ? "3px solid #7c3aed" : "3px solid transparent",
            color: tab === "bajas" ? "#7c3aed" : "#64748b", background: "none" }}>
          <UserX className="w-4 h-4" style={{ display: "inline", verticalAlign: "middle", marginRight: "6px" }} />
          Bajas con deuda {bajas.length > 0 && <span style={{ fontSize: "12px", backgroundColor: "#f3e8ff", color: "#7c3aed", padding: "1px 6px", borderRadius: "10px", marginLeft: "6px" }}>{bajas.length}</span>}
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
            {tab === "activos" ? "Cobros pendientes presenciales" : "Bajas con deuda irregular"}
          </h3>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <Search className="w-4 h-4" style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: "28px", padding: "5px 8px 5px 28px", fontSize: "12px", border: "1px solid #e2e8f0", borderRadius: "6px", width: "180px" }} />
          </div>
          <select value={filtroMetodo} onChange={(e) => setFiltroMetodo(e.target.value)}
            style={{ fontSize: "12px", padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
            <option value="">Todos los métodos</option>
            <option value="efectivo">Efectivo</option>
            <option value="tarjeta">Tarjeta</option>
            <option value="transferencia">Transferencia</option>
          </select>
        </div>
      </div>

      {loading && <div style={{ padding: "20px", textAlign: "center", color: "#94a3b8" }}>Cargando...</div>}
      {fetchError && <div style={{ padding: "16px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#dc2626", fontSize: "13px" }}><strong>Error:</strong> {fetchError}</div>}

      {!loading && renderTable(deudores, tab === "activos" ? "Sin cobros pendientes" : "Sin bajas con deuda")}

      {/* Modal Pagar */}
      {pagarModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setPagarModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "380px", boxShadow: "0 8px 30px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: 700 }}>Confirmar pago</h3>
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
              {pagarModal.nombre} — {pagarModal.meses_texto} — {pagarModal.total_importe.toFixed(2)}€
            </p>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>Método de pago</label>
              <select value={pagarMetodo} onChange={e => setPagarMetodo(e.target.value)}
                style={{ width: "100%", padding: "8px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
                <option value="bizum">Bizum</option>
              </select>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px" }}>Fecha de cobro</label>
              <input type="date" value={pagarFecha} onChange={e => setPagarFecha(e.target.value)}
                style={{ width: "100%", padding: "8px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px" }} />
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setPagarModal(null)}
                style={{ padding: "8px 16px", fontSize: "13px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#fff", cursor: "pointer" }}>Cancelar</button>
              <button onClick={handlePagar} disabled={pagando}
                style={{ padding: "8px 20px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "6px",
                  background: pagando ? "#9ca3af" : "#16a34a", color: "#fff", cursor: pagando ? "not-allowed" : "pointer" }}>
                {pagando ? "Procesando..." : `Confirmar ${pagarModal.cobro_ids?.length || 0} cobro(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeudaPresencialPanel;
